<script setup>
import { message } from 'ant-design-vue'
import Header from '@/components/Header.vue'
import DesignFooter from '@/components/DesignFooter.vue'

const asset = (name) => `${import.meta.env.BASE_URL}image/datahub-redesign/${name}`
const showComingSoon = () => message.info('DataHub 工作台暂未开放，敬请期待。')
const labels = ['数据集管理', '技能资产', '组织协作', '权限治理']
const capabilities = [
  { title: '数据集资产管理', description: '围绕数据集完成创建、文件管理、介绍维护、在线预览和上传交付。', items: ['仓库式数据集空间', '文件树与预览', '创建、上传、设置闭环'] },
  { title: 'Skills Hub', description: '集中沉淀可复用的 AI 技能资产，支持检测、反馈和版本化协作。', items: ['技能介绍与文件', '安全检测记录', '反馈与迭代协同'] },
  { title: '组织协作空间', description: '用组织承载团队资产和成员关系，帮助跨角色成员共同推进项目。', items: ['组织创建与管理', '成员角色协作', '资产归属清晰'] },
  { title: '权限与治理', description: '结合菜单权限、登录状态和资产可见性策略，减少敏感数据误触达。', items: ['菜单权限控制', '下载策略管理', '操作权限收敛'] },
]
const steps = [
  { title: '创建资产', description: '选择个人或组织命名空间，建立数据集或技能资产的基础信息。' },
  { title: '上传维护', description: '围绕文件、介绍、预览和设置持续更新，让资产内容保持可追踪。' },
  { title: '发现复用', description: '通过首页入口和列表筛选快速定位可信资产，减少重复建设。' },
  { title: '治理交付', description: '按登录状态、组织角色和菜单权限控制可见范围与操作边界。' },
]
const previews = [
  { title: '人脸检测', image: 'face.webp' },
  { title: '安全帽检测', image: 'helmet.webp' },
  { title: 'OCR 文字识别', image: 'ocr.webp' },
]
</script>

<template>
  <Header design-always hide-banner />
  <main class="datahub-page">
    <section class="datahub-hero" aria-labelledby="datahub-title">
      <div class="datahub-container hero-layout">
        <div class="hero-copy">
          <h1 id="datahub-title">DataHub</h1>
          <p class="hero-description">将数据集、Skills Hub、组织空间和权限策略统一到一个工作台，帮助团队快速发现可信资产，完成上传、预览、检测、协作和交付。</p>
          <ul class="hero-labels" aria-label="平台能力"><li v-for="label in labels" :key="label">{{ label }}</li></ul>
          <div class="hero-actions">
            <button class="datahub-button primary-button" v-track="'datahub > workspace'" type="button" @click="showComingSoon">敬请期待</button>
          </div>
        </div>
        <div class="hero-art"><img :src="asset('hero.webp')" alt="DataHub AI项目资产协作工作台主题示意" fetchpriority="high" /></div>
      </div>
    </section>

    <section class="datahub-section" aria-labelledby="capabilities-title">
      <div class="datahub-container">
        <div class="section-heading">
          <h2 id="capabilities-title">围绕 AI 项目资产的完整协作链路</h2>
          <p>围绕数据集、技能资产和组织权限建立统一入口，团队可以用同一种方式沉淀、搜索和交付 AI 项目资产。</p>
        </div>
        <div class="capability-grid">
          <article v-for="item in capabilities" :key="item.title" class="capability-card">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
            <ul class="capability-items"><li v-for="point in item.items" :key="point">{{ point }}</li></ul>
          </article>
        </div>
      </div>
    </section>

    <section class="datahub-section workflow-section" aria-labelledby="workflow-title">
      <div class="datahub-container">
        <div class="section-heading">
          <h2 id="workflow-title">从资产创建到团队复用，一条路径完成</h2>
          <p>从命名空间、文件维护到权限治理，关键动作都沿着资产生命周期展开。</p>
        </div>
        <div class="workflow-layout">
          <figure class="workspace-figure" aria-label="DataHub 资产工作台界面示意，非实际操作界面">
            <div class="workspace-preview">
              <div class="workspace-title">资产工作台</div>
              <div class="workspace-layout">
                <div class="workspace-sidebar" aria-hidden="true">
                  <strong>AI 项目资产</strong>
                  <span class="sidebar-current">数据集</span><span>技能</span><span>组织</span>
                </div>
                <div class="workspace-content">
                  <strong class="workspace-subtitle">数据集文件空间</strong>
                  <div class="workspace-tabs" aria-hidden="true"><span>介绍</span><span>文件</span><span class="tab-current">预览</span><span>上传</span><span>设置</span></div>
                  <div class="preview-grid">
                    <div v-for="preview in previews" :key="preview.title" class="preview-card"><img :src="asset(preview.image)" :alt="preview.title + '数据集预览示例'" loading="lazy" /><span>{{ preview.title }}</span></div>
                  </div>
                  <div class="workspace-details">
                    <div class="detail-panel"><strong>Skills Hub 交付流</strong><p>检测、反馈、文件、版本协同</p><div class="placeholder-lines" aria-hidden="true"><i></i><i></i><i></i></div></div>
                    <div class="detail-panel"><strong>权限与治理</strong><ul><li v-for="point in capabilities[3].items" :key="point">{{ point }}</li></ul></div>
                  </div>
                </div>
              </div>
            </div>
          </figure>
          <ol class="workflow-list">
            <li v-for="(step, index) in steps" :key="step.title">
              <span class="step-number">{{ String(index + 1).padStart(2, '0') }}</span>
              <div><h3>{{ step.title }}</h3><p>{{ step.description }}</p></div>
            </li>
          </ol>
        </div>
      </div>
    </section>
  </main>
  <DesignFooter />
</template>

<style scoped>
.datahub-page { width: 100%; min-width: 0; color: #14233b; line-height: 1.5; }
.datahub-page, .datahub-page *, .datahub-page *::before, .datahub-page *::after { box-sizing: border-box; }
.datahub-page h1, .datahub-page h2, .datahub-page h3, .datahub-page p, .datahub-page ul, .datahub-page ol, .datahub-page figure { margin: 0; }
.datahub-page img { display: block; max-width: 100%; }
.datahub-container { width: min(1440px, calc(100% - 48px)); margin: 0 auto; }
.datahub-hero { display: flex; align-items: center; min-height: 416px; padding: 48px 0; background: #f4f5f7; }
.hero-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: center; }
.hero-copy, .hero-art { min-width: 0; }
.hero-copy h1 { margin-top: 4px; font-size: 30px; font-weight: 600; line-height: 1.3; }
.hero-description { margin-top: 16px !important; color: #5c6c81; font-size: 16px; line-height: 1.75; }
.hero-labels { display: flex; flex-wrap: wrap; gap: 12px; padding: 0; margin-top: 16px !important; list-style: none; }
.hero-labels li { padding: 6px 12px; border-radius: 4px; background: #e9edf2; color: #617089; font-size: 14px; line-height: 22px; }
.hero-actions { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 32px; }
.datahub-button { display: inline-flex; justify-content: center; align-items: center; min-height: 48px; padding: 11px 20px; border: 1px solid transparent; border-radius: 4px; font: inherit; font-size: 16px; cursor: pointer; }
.primary-button { background: #ed000c; color: #fff; }
.primary-button:hover { background: #cb000a; }
.secondary-button { border-color: #dce2ea; background: #fff; color: #14233b; }
.secondary-button:hover { border-color: #ed000c; color: #ed000c; }
.datahub-button:focus-visible { outline: 2px solid #ed000c; outline-offset: 4px; }
.hero-art { position: relative; }
.hero-art img { width: 100%; height: auto; object-fit: contain; }
.hero-art::after { content: ''; position: absolute; inset: 0; pointer-events: none; background: linear-gradient(90deg, #f4f5f7, transparent 7%, transparent 93%, #f4f5f7), linear-gradient(180deg, #f4f5f7, transparent 8%, transparent 92%, #f4f5f7); }
.datahub-section { padding: 64px 0; }
.section-heading { margin-bottom: 32px; text-align: center; }
.section-heading h2 { font-size: 24px; font-weight: 600; line-height: 1.4; }
.section-heading p { margin-top: 16px; color: #617089; font-size: 16px; line-height: 26px; }
.capability-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 24px; align-items: stretch; }
.capability-card { display: flex; flex-direction: column; gap: 16px; min-width: 0; padding: 28px; border-radius: 12px; background: #f4f7fb; }
.datahub-page h3 { font-size: 18px; font-weight: 600; line-height: 28px; }
.capability-card p { flex: 1; color: #617089; font-size: 16px; line-height: 28px; }
.capability-items { display: grid; gap: 8px; padding: 0; list-style: none; }
.capability-items li { position: relative; padding-left: 16px; font-size: 16px; line-height: 24px; }
.capability-items li::before { content: ''; position: absolute; left: 0; top: 10px; width: 4px; height: 4px; border-radius: 50%; background: #ed000c; }
.workflow-section { background: #f4f7fb; }
.workflow-layout { display: grid; grid-template-columns: minmax(0, 816fr) minmax(0, 560fr); gap: 64px; align-items: center; }
.workspace-figure { display: flex; align-items: center; justify-content: center; min-width: 0; min-height: 520px; padding: 40px; border-radius: 12px; background: #f6f9fc; }
.workspace-preview { width: 100%; padding: 20px; border: 1px solid #e1e6ee; border-radius: 8px; background: #fff; box-shadow: 0 10px 24px rgb(33 48 71 / 9%); }
.workspace-title { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; font-size: 15px; font-weight: 600; }
.workspace-title::before { content: ''; width: 4px; height: 20px; border-radius: 2px; background: #ed000c; }
.workspace-layout { display: grid; grid-template-columns: 92px minmax(0, 1fr); gap: 16px; }
.workspace-sidebar { display: flex; flex-direction: column; gap: 16px; padding: 12px 8px; border-radius: 6px; background: #f5f7fb; font-size: 12px; color: #78859a; }
.workspace-sidebar strong { color: #17283f; font-size: 12px; }
.workspace-sidebar span { padding: 6px 8px; }
.workspace-sidebar .sidebar-current { color: #ed000c; background: #ffecef; font-weight: 600; }
.workspace-content { display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.workspace-subtitle { font-size: 16px; color: #17283f; }
.workspace-tabs { display: flex; flex-wrap: wrap; gap: 16px; font-size: 12px; color: #718096; }
.tab-current { color: #ed000c; font-weight: 600; }
.preview-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.preview-card { display: flex; flex-direction: column; gap: 6px; min-width: 0; padding: 6px; border-radius: 5px; background: #f5f7fb; color: #5c6e85; font-size: 11px; }
.preview-card img { width: 100%; aspect-ratio: 153 / 80; object-fit: cover; border-radius: 4px; }
.workspace-details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.detail-panel { min-width: 0; padding: 12px; border-radius: 6px; background: #f6f8fb; color: #6e7e92; font-size: 11px; }
.detail-panel strong { display: block; margin-bottom: 8px; color: #17283f; font-size: 13px; }
.detail-panel ul { display: grid; gap: 6px; padding: 0; list-style: none; }
.detail-panel li::before { content: ''; display: inline-block; width: 4px; height: 4px; margin-right: 8px; border-radius: 1px; background: #ed000c; vertical-align: middle; }
.placeholder-lines { display: grid; gap: 6px; margin-top: 10px; }
.placeholder-lines i { display: block; height: 4px; border-radius: 2px; background: #d2dbe7; }
.placeholder-lines i:nth-child(2) { width: 75%; }
.placeholder-lines i:nth-child(3) { width: 88%; }
.workflow-list { display: grid; min-width: 0; padding: 0; list-style: none; }
.workflow-list > li { position: relative; display: flex; align-items: flex-start; gap: 20px; padding-bottom: 28px; }
.workflow-list > li:last-child { padding-bottom: 0; }
.workflow-list > li:not(:last-child)::before { content: ''; position: absolute; top: 36px; bottom: 8px; left: 14px; width: 1px; background: #dce2ea; }
.step-number { display: inline-flex; justify-content: center; align-items: center; flex: 0 0 28px; width: 28px; height: 28px; border: 1px solid #dce2ea; border-radius: 50%; background: #fff; font-size: 14px; }
.workflow-list > li:first-child .step-number { color: #ed000c; }
.workflow-list p { margin-top: 8px; color: #617089; font-size: 16px; line-height: 24px; }
@media (min-width: 768px) { .hero-copy h1 { font-size: 36px; } .hero-description { font-size: 18px; } .section-heading h2 { font-size: 26px; } }
@media (min-width: 992px) { .hero-copy h1 { font-size: 40px; } .section-heading h2 { font-size: 28px; } }
@media (min-width: 1200px) { .hero-copy h1 { font-size: 44px; } .section-heading h2 { font-size: 30px; } }
@media (min-width: 1400px) { .section-heading h2 { font-size: 32px; } }
@media (max-width: 1199px) { .capability-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .workflow-layout { gap: 32px; } .workspace-figure { padding: 24px; } .workspace-layout { grid-template-columns: minmax(0, 1fr); } .workspace-sidebar { flex-direction: row; flex-wrap: wrap; align-items: center; gap: 8px; } }
@media (max-width: 991px) { .workflow-layout { grid-template-columns: minmax(0, 1fr); } .workspace-figure { min-height: 440px; } .workspace-layout { grid-template-columns: 100px minmax(0, 1fr); } .workspace-sidebar { flex-direction: column; align-items: stretch; gap: 16px; } }
@media (max-width: 767px) { .datahub-container { width: calc(100% - 32px); } .datahub-hero, .datahub-section { padding: 40px 0; } .hero-layout, .capability-grid { grid-template-columns: minmax(0, 1fr); } .hero-art { width: min(100%, 520px); margin: 8px auto 0; } .workspace-figure { padding: 12px; min-height: 0; } .workspace-preview { padding: 12px; } .workspace-layout { grid-template-columns: minmax(0, 1fr); } .workspace-sidebar { flex-direction: row; flex-wrap: wrap; align-items: center; gap: 8px; } .workspace-details { grid-template-columns: minmax(0, 1fr); } .preview-grid { gap: 6px; } .workspace-tabs { gap: 12px; } }
</style>
